import React from 'react';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import type { GroupedResults } from '../types';
import { en, th } from '../locales';
import ResultGroup from './ResultGroup';
import { testIds } from '@/test/testIds';

interface ResultsPanelProps {
  results: GroupedResults | undefined;
  isFetching: boolean;
  hasSearched: boolean;
  onClose: () => void;
}

const ResultsPanel: React.FC<ResultsPanelProps> = ({ results, isFetching, hasSearched, onClose }) => {
  const { t } = useTranslation({ en, th });
  const groups = Object.entries(results ?? {});
  const isEmpty = hasSearched && !isFetching && groups.length === 0;

  return (
    <div data-testid={testIds.globalSearch.panel}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{t('resultsTitle')}</h2>
        <div className="flex items-center gap-2">
          {isFetching && (
            <Loader2 className="h-4 w-4 animate-spin text-primary" data-testid={testIds.globalSearch.loading} />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            data-testid={testIds.globalSearch.close}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!hasSearched && <p className="py-6 text-center text-sm text-muted-foreground">{t('typeToSearch')}</p>}

      {isEmpty && <p className="py-6 text-center text-sm text-muted-foreground">{t('noData')}</p>}

      {groups.length > 0 && (
        <div className="space-y-5">
          {groups.map(([documentType, hits]) => (
            <ResultGroup key={documentType} documentType={documentType} hits={hits} onNavigated={onClose} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResultsPanel;
